import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowSquareOut, DownloadSimple, FileText, Spinner, X, MagnifyingGlassPlus,
  MagnifyingGlassMinus, ArrowClockwise, DotsSixVertical,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";

const InvoiceViewerContext = createContext(null);

export function useInvoiceViewer() {
  const ctx = useContext(InvoiceViewerContext);
  if (!ctx) throw new Error("useInvoiceViewer must be used within <InvoiceViewerProvider>");
  return ctx;
}

const kindFromMime = (ct) => {
  if (!ct) return null;
  const c = ct.toLowerCase();
  if (c.startsWith("image/")) return "image";
  if (c === "application/pdf" || c.includes("pdf")) return "pdf";
  return "other";
};

const MIN_SCALE = 0.25;
const MAX_SCALE = 5;
const MIN_W = 280;
const MIN_H = 260;
const EDGE_MARGIN = 8;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function ImageViewer({ src, name, scale, setScale }) {
  const pinchStart = useRef(null);

  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.0025;
    setScale((s) => clamp(s + delta * s, MIN_SCALE, MAX_SCALE));
  };
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      pinchStart.current = { dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), scale };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchStart.current) {
      const [a, b] = e.touches;
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      setScale(clamp(pinchStart.current.scale * (dist / pinchStart.current.dist), MIN_SCALE, MAX_SCALE));
      e.preventDefault();
    }
  };
  const onTouchEnd = (e) => { if (e.touches.length < 2) pinchStart.current = null; };

  return (
    <div
      className="w-full h-full overflow-auto bg-slate-100"
      onWheel={onWheel}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{ touchAction: "pan-x pan-y" }}
      data-testid="invoice-viewer-image-wrap"
    >
      <div className="min-w-full min-h-full flex items-center justify-center p-2">
        <img
          src={src}
          alt={name}
          draggable={false}
          className="select-none"
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "center center",
            transition: "transform 0.08s ease-out",
            maxWidth: "none",
          }}
          data-testid="invoice-viewer-image"
        />
      </div>
    </div>
  );
}

// 8-way resize + drag handles
// mode: 'move' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
function useDragResize(frame, setFrame) {
  const stateRef = useRef(null);

  const begin = useCallback((mode, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    stateRef.current = { mode, startX, startY, startFrame: { ...frame } };

    const onMove = (ev) => {
      const st = stateRef.current;
      if (!st) return;
      const dx = ev.clientX - st.startX;
      const dy = ev.clientY - st.startY;
      const sf = st.startFrame;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let { left, top, width, height } = sf;

      if (st.mode === "move") {
        left = clamp(sf.left + dx, EDGE_MARGIN, vw - sf.width - EDGE_MARGIN);
        top = clamp(sf.top + dy, EDGE_MARGIN, vh - sf.height - EDGE_MARGIN);
      } else {
        if (st.mode.includes("e")) {
          width = clamp(sf.width + dx, MIN_W, vw - sf.left - EDGE_MARGIN);
        }
        if (st.mode.includes("w")) {
          const nextW = clamp(sf.width - dx, MIN_W, sf.left + sf.width - EDGE_MARGIN);
          left = sf.left + (sf.width - nextW);
          width = nextW;
        }
        if (st.mode.includes("s")) {
          height = clamp(sf.height + dy, MIN_H, vh - sf.top - EDGE_MARGIN);
        }
        if (st.mode.includes("n")) {
          const nextH = clamp(sf.height - dy, MIN_H, sf.top + sf.height - EDGE_MARGIN);
          top = sf.top + (sf.height - nextH);
          height = nextH;
        }
      }
      setFrame({ left, top, width, height });
    };
    const onUp = () => {
      stateRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    document.body.style.userSelect = "none";
    const cursorMap = { move: "grabbing", n: "ns-resize", s: "ns-resize", e: "ew-resize", w: "ew-resize", ne: "nesw-resize", sw: "nesw-resize", nw: "nwse-resize", se: "nwse-resize" };
    document.body.style.cursor = cursorMap[mode] || "default";
  }, [frame, setFrame]);

  return begin;
}

const RESIZE_HANDLES = [
  { k: "n",  cls: "left-2 right-2 top-0 h-1.5 cursor-ns-resize" },
  { k: "s",  cls: "left-2 right-2 bottom-0 h-1.5 cursor-ns-resize" },
  { k: "e",  cls: "top-2 bottom-2 right-0 w-1.5 cursor-ew-resize" },
  { k: "w",  cls: "top-2 bottom-2 left-0 w-1.5 cursor-ew-resize" },
  { k: "nw", cls: "left-0 top-0 w-3 h-3 cursor-nwse-resize" },
  { k: "ne", cls: "right-0 top-0 w-3 h-3 cursor-nesw-resize" },
  { k: "sw", cls: "left-0 bottom-0 w-3 h-3 cursor-nesw-resize" },
  { k: "se", cls: "right-0 bottom-0 w-3 h-3 cursor-nwse-resize" },
];

function Viewer({ fileId, filename, onClose, frame, setFrame }) {
  const [kind, setKind] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [resolvedName, setResolvedName] = useState(filename || "Invoice");
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1);
  const beginDrag = useDragResize(frame, setFrame);

  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    const createdUrls = [];
    (async () => {
      try {
        const url = `${API}/invoices/${fileId}`;
        const r = await fetch(url, { method: "GET", credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const cd = r.headers.get("content-disposition") || "";
        const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        if (m) {
          try { setResolvedName(decodeURIComponent(m[1])); } catch { setResolvedName(m[1]); }
        } else if (filename) {
          setResolvedName(filename);
        }
        const blob = await r.blob();
        if (cancelled) return;
        const mime = blob.type || r.headers.get("content-type") || "";
        const k = kindFromMime(mime) || "other";
        const objUrl = URL.createObjectURL(blob);
        createdUrls.push(objUrl);
        setBlobUrl(objUrl);
        setKind(k);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Could not load file");
          setKind("other");
        }
      }
    })();
    return () => {
      cancelled = true;
      createdUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [fileId, filename]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const openUrl = useMemo(() => `${API}/invoices/${fileId}`, [fileId]);
  const canZoom = kind === "image";

  return (
    <div
      className="pointer-events-auto absolute flex flex-col bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden"
      style={{ left: frame.left, top: frame.top, width: frame.width, height: frame.height }}
      data-testid="invoice-viewer-modal"
      role="dialog"
      aria-label="Invoice preview"
    >
      {/* 8 resize handles */}
      {RESIZE_HANDLES.map((h) => (
        <div
          key={h.k}
          className={`absolute z-20 ${h.cls}`}
          onPointerDown={(e) => beginDrag(h.k, e)}
          data-testid={`invoice-viewer-resize-${h.k}`}
        />
      ))}

      {/* Header — drag handle */}
      <div
        className="relative z-10 flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 select-none cursor-grab active:cursor-grabbing"
        onPointerDown={(e) => {
          // only start drag when not clicking a button/anchor inside the header
          if (e.target.closest("button, a")) return;
          beginDrag("move", e);
        }}
        data-testid="invoice-viewer-drag-handle"
      >
        <DotsSixVertical size={14} className="shrink-0 text-slate-400" />
        <FileText size={14} weight="duotone" className="shrink-0 text-slate-500" />
        <div
          className="flex-1 truncate text-xs font-medium text-slate-800"
          title={resolvedName}
          data-testid="invoice-viewer-filename"
        >
          {resolvedName}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-800"
          title="Close"
          data-testid="invoice-viewer-close"
        >
          <X size={14} />
        </button>
      </div>

      {/* Toolbar */}
      <div className="relative z-10 flex items-center justify-between gap-2 px-2 py-1.5 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-1">
          {canZoom && (
            <>
              <button
                type="button"
                onClick={() => setScale((s) => clamp(s - 0.25, MIN_SCALE, MAX_SCALE))}
                className="p-1 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-40"
                disabled={scale <= MIN_SCALE}
                title="Zoom out"
                data-testid="invoice-viewer-zoom-out"
              >
                <MagnifyingGlassMinus size={14} />
              </button>
              <div
                className="text-[10px] font-mono text-slate-500 w-10 text-center tabular-nums"
                data-testid="invoice-viewer-zoom-level"
              >
                {Math.round(scale * 100)}%
              </div>
              <button
                type="button"
                onClick={() => setScale((s) => clamp(s + 0.25, MIN_SCALE, MAX_SCALE))}
                className="p-1 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-40"
                disabled={scale >= MAX_SCALE}
                title="Zoom in"
                data-testid="invoice-viewer-zoom-in"
              >
                <MagnifyingGlassPlus size={14} />
              </button>
              <button
                type="button"
                onClick={() => setScale(1)}
                className="p-1 rounded hover:bg-slate-100 text-slate-500"
                title="Reset zoom"
                data-testid="invoice-viewer-zoom-reset"
              >
                <ArrowClockwise size={13} />
              </button>
            </>
          )}
          {kind === "pdf" && (
            <div className="text-[10px] text-slate-400 px-1">PDF — use viewer's controls to zoom</div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <a
            href={openUrl}
            target="_blank"
            rel="noreferrer"
            className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-blue-600"
            title="Open in new tab"
            data-testid="invoice-viewer-open-new-tab"
          >
            <ArrowSquareOut size={13} />
          </a>
          <a
            href={blobUrl || openUrl}
            download={resolvedName}
            className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-emerald-600"
            title="Download"
            data-testid="invoice-viewer-download"
          >
            <DownloadSimple size={13} />
          </a>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 relative" data-testid="invoice-viewer-body">
        {!kind && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs gap-2">
            <Spinner size={14} className="animate-spin" /> Loading…
          </div>
        )}
        {kind === "image" && blobUrl && (
          <ImageViewer src={blobUrl} name={resolvedName} scale={scale} setScale={setScale} />
        )}
        {kind === "pdf" && blobUrl && (
          <iframe
            src={blobUrl}
            title={resolvedName}
            className="w-full h-full bg-white"
            data-testid="invoice-viewer-pdf"
          />
        )}
        {kind === "other" && (
          <div className="h-full flex flex-col items-center justify-center p-4 text-center gap-3">
            <FileText size={36} weight="duotone" className="text-slate-400" />
            <div className="text-xs text-slate-600">
              {error ? "Could not load this file." : "This file type can't be previewed inline."}
            </div>
            <a
              href={blobUrl || openUrl}
              download={resolvedName}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
              data-testid="invoice-viewer-download-primary"
            >
              <DownloadSimple size={14} /> Download
            </a>
            <a
              href={openUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-slate-500 hover:text-blue-600 inline-flex items-center gap-1"
            >
              <ArrowSquareOut size={12} /> Open in new tab
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

const defaultFrame = () => {
  const w = 400;
  const h = 500;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  return {
    width: w,
    height: h,
    left: EDGE_MARGIN,
    top: Math.max(EDGE_MARGIN, vh - h - EDGE_MARGIN),
  };
};

export function InvoiceViewerProvider({ children }) {
  const [state, setState] = useState({ open: false, fileId: null, filename: null });
  // Persist position & size across opens for a sticky, familiar PiP.
  const [frame, setFrame] = useState(defaultFrame);

  // Re-clamp into viewport if window shrinks below the current frame.
  useEffect(() => {
    const onResize = () => {
      setFrame((f) => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const width = Math.min(f.width, Math.max(MIN_W, vw - 2 * EDGE_MARGIN));
        const height = Math.min(f.height, Math.max(MIN_H, vh - 2 * EDGE_MARGIN));
        const left = clamp(f.left, EDGE_MARGIN, vw - width - EDGE_MARGIN);
        const top = clamp(f.top, EDGE_MARGIN, vh - height - EDGE_MARGIN);
        return { left, top, width, height };
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const openInvoice = useCallback((fileId, filename) => {
    if (!fileId) return;
    setState({ open: true, fileId, filename: filename || null });
  }, []);
  const closeInvoice = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  const value = useMemo(() => ({ openInvoice, closeInvoice }), [openInvoice, closeInvoice]);

  return (
    <InvoiceViewerContext.Provider value={value}>
      {children}
      {state.open && (
        <div
          className="fixed inset-0 z-[9999] pointer-events-none"
          data-testid="invoice-viewer-root"
        >
          <Viewer
            key={state.fileId}
            fileId={state.fileId}
            filename={state.filename}
            onClose={closeInvoice}
            frame={frame}
            setFrame={setFrame}
          />
        </div>
      )}
    </InvoiceViewerContext.Provider>
  );
}

export function InvoiceLinkButton({ fileId, filename, children, className = "", testid }) {
  const { openInvoice } = useInvoiceViewer();
  if (!fileId) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openInvoice(fileId, filename); }}
      className={className}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
